import { useState, useCallback, useMemo } from 'react';
import type { UseMultiStepFormReturn } from './types';

/**
 * Custom hook for managing multi-step form UI state
 * Pure UI state management - no business logic
 * @param totalSteps Total number of steps in the form
 * @param initialStepIndex Initial step index (defaults to 0)
 * @returns Object with current step state and navigation functions
 */
export function useMultiStepForm(
    totalSteps: number,
    initialStepIndex: number = 0,
): UseMultiStepFormReturn {
    const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
    const [isStepValid, setIsStepValid] = useState(true);

    // Memoized computed values
    const isFirstStep = useMemo(
        () => currentStepIndex === 0,
        [currentStepIndex],
    );
    const isLastStep = useMemo(
        () => (totalSteps === 0 ? true : currentStepIndex === totalSteps - 1),
        [currentStepIndex, totalSteps],
    );
    const isCompleted = useMemo(
        () => currentStepIndex >= totalSteps,
        [currentStepIndex, totalSteps],
    );

    /**
     * Navigate to the next step
     */
    const goToNext = useCallback(() => {
        setCurrentStepIndex((prev) => {
            if (prev >= totalSteps - 1) {
                // Don't go beyond the last step
                return prev;
            }
            return prev + 1;
        });
    }, [totalSteps]);

    /**
     * Navigate to the previous step
     */
    const goToPrevious = useCallback(() => {
        setCurrentStepIndex((prev) => {
            if (prev <= 0) {
                // Don't go before the first step
                return prev;
            }
            return prev - 1;
        });
    }, []);

    /**
     * Navigate to a specific step by index
     * @param stepIndex Target step index (0-based)
     */
    const goToStep = useCallback(
        (stepIndex: number) => {
            if (stepIndex < 0 || stepIndex >= totalSteps) {
                console.warn(
                    `Invalid step index: ${stepIndex}. Must be between 0 and ${totalSteps - 1}`,
                );
                return;
            }
            setCurrentStepIndex(stepIndex);
        },
        [totalSteps],
    );

    /**
     * Set whether the current step is valid
     * @param isValid Whether the current step is valid
     */
    const setStepValid = useCallback((isValid: boolean) => {
        setIsStepValid(isValid);
    }, []);

    return {
        currentStepIndex,
        totalSteps,
        isFirstStep,
        isLastStep,
        isCompleted,
        isStepValid,
        goToNext,
        goToPrevious,
        goToStep,
        setStepValid,
    };
}
